/**
 * Teams Hub API Client
 * 
 * All API calls for team communication features.
 */

import { getDefaultClient } from '@quotech/api'

// =============================================================================
// TYPES
// =============================================================================

export interface Channel {
  id: string
  type: 'group' | 'direct' | 'job'
  name: string
  description?: string
  job_id?: number
  other_user?: {
    id: number
    name: string
    email: string
    is_online?: boolean
  }
  member_count: number
  unread_count: number
  last_message?: {
    id: string
    content: string
    sender_name: string
    created_at: string
  }
  is_muted: boolean
  is_pinned: boolean
  updated_at: string
}

export interface Message {
  id: string
  sender: {
    id: number | null
    name: string
    email?: string
  }
  content: string
  reply_to?: {
    id: string
    sender_name: string
    content: string
  }
  attachment_ids: string[]
  mentions: number[]
  is_edited: boolean
  is_own: boolean
  created_at: string
}

export interface User {
  id: number
  name: string
  email: string
  is_online: boolean
  status: 'online' | 'busy' | 'away' | 'offline'
  custom_status?: string
  is_self?: boolean
  role?: string
  department?: string
  avatar?: string
}

export interface Note {
  id: number
  job_id: number
  job_name?: string
  text: string
  pagename: string
  person_responsible?: string
  created_by: {
    id: number
    name: string
    email: string
  }
  created_at: string
  updated_at: string
  is_pinned: boolean
  thread_count: number
  mentions: number[]
}

export interface Announcement {
  id: string
  title: string
  content: string
  author: {
    id: number
    name: string
  }
  is_pinned: boolean
  read_count: number
  created_at: string
}

export interface Activity {
  id: string
  type: 'message' | 'mention' | 'note' | 'announcement' | 'channel_created'
  actor: {
    id: number
    name: string
  }
  target?: {
    type: 'channel' | 'user' | 'job' | 'note'
    id: string | number
    name: string
  }
  content?: string
  created_at: string
}

// =============================================================================
// API CLIENT
// =============================================================================

const getClient = () => getDefaultClient()

// =============================================================================
// CHANNELS API
// =============================================================================

export const channelsApi = {
  list: async (): Promise<Channel[]> => {
    const { data } = await getClient().get('/api/teams/channels/')
    return Array.isArray(data) ? data : []
  },
  
  get: async (id: string): Promise<Channel & { members: User[] }> => {
    const { data } = await getClient().get(`/api/teams/channels/${id}/`)
    return data
  },
  
  create: async (params: {
    type: 'group' | 'job'
    name: string
    description?: string
    member_ids?: number[]
    job_id?: number
  }): Promise<Channel> => {
    const { data } = await getClient().post('/api/teams/channels/', params)
    return data
  },
  
  leave: async (id: string): Promise<void> => {
    await getClient().delete(`/api/teams/channels/${id}/`)
  },
  
  addMembers: async (id: string, memberIds: number[]): Promise<void> => {
    await getClient().post(`/api/teams/channels/${id}/members/`, { member_ids: memberIds })
  },
  
  removeMembers: async (id: string, memberIds: number[]): Promise<void> => {
    await getClient().delete(`/api/teams/channels/${id}/members/`, { data: { member_ids: memberIds } })
  },
}

// =============================================================================
// MESSAGES API
// =============================================================================

export const messagesApi = {
  list: async (channelId: string, before?: string, limit = 50): Promise<{ messages: Message[], has_more: boolean }> => {
    const params = new URLSearchParams()
    if (before) params.set('before', before)
    params.set('limit', String(limit))
    
    const { data } = await getClient().get(`/api/teams/channels/${channelId}/messages/?${params}`)
    return data
  },
  
  send: async (channelId: string, content: string, replyTo?: string): Promise<Message> => {
    const { data } = await getClient().post(`/api/teams/channels/${channelId}/messages/`, {
      content,
      reply_to: replyTo,
    })
    return data
  },
  
  edit: async (channelId: string, messageId: string, content: string): Promise<Message> => {
    const { data } = await getClient().patch(`/api/teams/channels/${channelId}/messages/${messageId}/`, { content })
    return data
  },
  
  delete: async (channelId: string, messageId: string): Promise<void> => {
    await getClient().delete(`/api/teams/channels/${channelId}/messages/${messageId}/`)
  },
}

// =============================================================================
// DIRECT MESSAGES API
// =============================================================================

export const dmApi = {
  getOrCreate: async (userId: number): Promise<{ id: string, other_user: User, created: boolean }> => {
    const { data } = await getClient().get(`/api/teams/dm/${userId}/`)
    return data
  },
}

// =============================================================================
// USERS API
// =============================================================================

export const usersApi = {
  list: async (): Promise<User[]> => {
    const { data } = await getClient().get('/api/teams/users/')
    return Array.isArray(data) ? data : []
  },
  
  updatePresence: async (status: 'online' | 'away' | 'busy', customStatus?: string): Promise<void> => {
    await getClient().post('/api/teams/presence/', { status, custom_status: customStatus })
  },
  
  getPresence: async (): Promise<{ status: string, custom_status: string, last_seen: string, is_online: boolean }> => {
    const { data } = await getClient().get('/api/teams/presence/')
    return data
  },
}

// =============================================================================
// NOTES API (Cross-Hub Integration)
// Uses /api/report/JobPageNotes/ endpoint for unified notes across platform
// =============================================================================

export const notesApi = {
  list: async (params?: { job_id?: number, page?: number, limit?: number }): Promise<{ notes: Note[], total: number }> => {
    const queryParams = new URLSearchParams()
    if (params?.job_id) queryParams.set('job', String(params.job_id))
    if (params?.page) queryParams.set('page', String(params.page))
    if (params?.limit) queryParams.set('limit', String(params.limit))
    
    // Use the main JobPageNotes endpoint
    const { data } = await getClient().get(`/api/report/JobPageNotes/?${queryParams}`)
    
    // Transform backend response to match our Note interface
    const notes = (Array.isArray(data) ? data : []).map((note: any) => ({
      id: note.id,
      job_id: note.job,
      job_name: note.job_name || `Job #${note.job}`,
      text: note.text || '',
      pagename: note.pagename || 'general',
      person_responsible: note.personResponsible || note.person_responsible,
      created_by: {
        id: note.created_by || note.sender,
        name: note.created_by_name || note.personResponsible || 'Unknown',
        email: note.created_by_email || '',
      },
      created_at: note.dateTime || note.created_at,
      updated_at: note.dateTime || note.updated_at,
      is_pinned: note.is_pinned || false,
      thread_count: note.reply_count || note.replies_count || 0,
      mentions: note.mentions || [],
    }))
    
    return {
      notes,
      total: notes.length,
    }
  },
  
  get: async (id: number): Promise<Note & { threads: Message[] }> => {
    const { data } = await getClient().get(`/api/report/JobPageNotes/${id}/`)
    return {
      id: data.id,
      job_id: data.job,
      job_name: data.job_name || `Job #${data.job}`,
      text: data.text || '',
      pagename: data.pagename || 'general',
      person_responsible: data.personResponsible,
      created_by: {
        id: data.created_by,
        name: data.created_by_name || data.personResponsible || 'Unknown',
        email: data.created_by_email || '',
      },
      created_at: data.dateTime,
      updated_at: data.dateTime,
      is_pinned: false,
      thread_count: data.reply_count || 0,
      mentions: [],
      threads: data.replies || [],
    }
  },
  
  create: async (params: { job_id: number, text: string, pagename?: string }): Promise<Note> => {
    const formData = new FormData()
    formData.append('job', String(params.job_id))
    formData.append('text', params.text)
    formData.append('pagename', params.pagename || 'general')
    
    const { data } = await getClient().post('/api/report/JobPageNotes/', formData)
    return {
      id: data.id,
      job_id: data.job,
      text: data.text,
      pagename: data.pagename,
      person_responsible: data.personResponsible,
      created_by: {
        id: data.created_by,
        name: data.created_by_name || 'You',
        email: '',
      },
      created_at: data.dateTime,
      updated_at: data.dateTime,
      is_pinned: false,
      thread_count: 0,
      mentions: [],
    }
  },
  
  reply: async (noteId: number, content: string): Promise<Message> => {
    const { data } = await getClient().post(`/api/report/notes/${noteId}/replies/`, { text: content })
    return data
  },
  
  markAsRead: async (noteId: number): Promise<void> => {
    await getClient().post(`/api/report/notes/${noteId}/read/`)
  },
}

// =============================================================================
// ANNOUNCEMENTS API
// =============================================================================

export const announcementsApi = {
  list: async (): Promise<Announcement[]> => {
    const { data } = await getClient().get('/api/teams/announcements/')
    return Array.isArray(data) ? data : []
  },
  
  create: async (params: { title: string, content: string, is_pinned?: boolean }): Promise<Announcement> => {
    const { data } = await getClient().post('/api/teams/announcements/', params)
    return data
  },
  
  markRead: async (id: string): Promise<void> => {
    await getClient().post(`/api/teams/announcements/${id}/read/`)
  },
}

// =============================================================================
// ACTIVITY API
// =============================================================================

export const activityApi = {
  list: async (limit = 50): Promise<Activity[]> => {
    const { data } = await getClient().get(`/api/teams/activity/?limit=${limit}`)
    return Array.isArray(data) ? data : []
  },
  
  getMentions: async (): Promise<Activity[]> => {
    const { data } = await getClient().get('/api/teams/mentions/')
    return Array.isArray(data) ? data : []
  },
}
